import {
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	input,
	output,
	type OutputEmitterRef,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

import type { Mut } from '@efficiently/shared-util-types';

import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import { EffBadgeComponent, EffIconButtonComponent } from '@efficiently/client-ui-kit';

import type { ClarityDialogFrameHeadingAlign } from '../models/clarity-dialog-frame-heading-align.type';
import type { ClarityDialogFrameHeadingLevel } from '../models/clarity-dialog-frame-heading-level.type';
import type { ClarityDialogFrameSurface } from '../models/clarity-dialog-frame-surface.type';
import type { ClarityOverlayTone } from '../models/clarity-overlay-tone.type';

const DEFAULT_SURFACE: ClarityDialogFrameSurface = 'compact';
const DEFAULT_TONE: ClarityOverlayTone = 'neutral';
const DEFAULT_CLOSE_LABEL = 'Close';
const DEFAULT_HEADING_LEVEL: ClarityDialogFrameHeadingLevel = 2;
const DEFAULT_HEADING_ALIGN: ClarityDialogFrameHeadingAlign = 'start';

/** Ported from Clarity `apps/web/src/shared/ui/dialog/DialogChrome.tsx` (commit `f9217bb5a`). */
/**
 * The chrome a Clarity overlay's body renders inside — heading, optional count, optional error band,
 * body and an optional footer.
 *
 * One component for both surfaces, not two. `surface` is the only difference between a dialog's
 * frame and a fly-in's: the regions, the order they appear in and the close affordance are identical,
 * and splitting them would mean two places to fix every heading, badge and error-band decision.
 * It is reflected to a host `data-*` attribute rather than to a class, so the geometry is switched by
 * the cascade and no branch on surface exists in TypeScript.
 *
 * It knows nothing about how it was opened. There is no `ClarityDialogRef` here and no overlay
 * import: {@link closed} is an output the body wires to whichever ref it holds, which is what lets the
 * same frame render in a dialog, in a fly-in, and in a showcase demo that opens neither.
 *
 * **The error band carries `role="alert"` on the band element itself.** Announcing a failure without
 * moving focus is the whole point of the region, and an `alert` bound to a wrapper that does not
 * render the message is read by nothing.
 */
@Component({
	selector: 'eff-clarity-dialog-frame',
	imports: [EffBadgeComponent, EffIconButtonComponent, MatTooltip, TestIdDirective],
	templateUrl: './clarity-dialog-frame.component.html',
	styleUrl: './clarity-dialog-frame.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[attr.data-surface]': 'surface()',
		'[attr.data-tone]': 'tone()',
		'[attr.data-clamp-heading]': 'clampHeading() ? "" : null',
	},
})
export class ClarityDialogFrameComponent {
	/** The overlay's visible title. */
	public readonly heading = input.required<string>();
	/** `compact` inside a dialog, `wide` inside a fly-in. */
	public readonly surface = input<ClarityDialogFrameSurface>(DEFAULT_SURFACE);
	/** `danger` for a destructive question; the frame carries no other semantic weight. */
	public readonly tone = input<ClarityOverlayTone>(DEFAULT_TONE);
	/** Rendered as a badge beside the heading. Absent means no badge element at all. */
	public readonly count = input<number>();
	/** Rendered in the inline error band. Clearing it removes the band from the DOM. */
	public readonly error = input<string>();
	/** `h2` by default; `1` for a hand-rolled modal that heads the outline while it is open. */
	public readonly headingLevel = input<ClarityDialogFrameHeadingLevel>(DEFAULT_HEADING_LEVEL);
	/** `center` for a dialog whose header carries nothing but the heading. */
	public readonly headingAlign = input<ClarityDialogFrameHeadingAlign>(DEFAULT_HEADING_ALIGN);
	/** `true` cuts the heading after two lines and shows the full text in a tooltip while it is cut. */
	public readonly clampHeading = input(false, { transform: booleanAttribute });
	/**
	 * `true` keeps the heading on one line, cut with an ellipsis, and shows the full text in a tooltip while it is cut —
	 * for a heading of unknown length over media whose height budget allows one heading line. Outranks
	 * {@link clampHeading}.
	 */
	public readonly truncateHeading = input(false, { transform: booleanAttribute });
	/**
	 * `false` renders no close control at all — for a body whose own footer is the only way out, and as
	 * brain's `DialogChrome` renders none for a dialog that may not be dismissed right now
	 * (`DialogChrome.tsx:101-112`). The header grid keeps its tracks, so the heading stays where it was.
	 */
	public readonly closable = input(true, { transform: booleanAttribute });
	/** The close control's accessible name — it has no visible text. */
	public readonly closeLabel = input<string>(DEFAULT_CLOSE_LABEL);

	/** The close control was pressed. The body decides what closing means. */
	public readonly closed: OutputEmitterRef<void> = output<void>();

	protected onHeadingEnter(heading: HTMLElement, tooltip: Mut<MatTooltip>): void {
		tooltip.disabled = this.truncateHeading()
			? heading.scrollWidth <= heading.clientWidth
			: heading.scrollHeight <= heading.clientHeight;
	}

	protected onClose(): void {
		this.closed.emit();
	}
}
